// src/notifications/hooks/useSidebarIndicators.js
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { markNotificationRead } from "../redux/notificationsThunks";
import useNotificationsSocket from "./useNotificationsSocket";
import {
  selectEffectiveUserId,
  selectPrincipalUserId,
} from "../../sous-profile/redux/selectors/sousProfileSelectors";
import { selectAuthUser } from "../../authPatient/redux/selectors/authSelectors";
import {
  computeIndicators,
  isUnderRoute,
  normId,
  INDICATOR,
} from "../hooks/sidebarIndicators";

const selectItems = (state) => state.notifications?.items;

/**
 * Live sidebar indicators derived from the shared notifications store.
 *
 * Real-time: useNotificationsSocket -> pushNotification -> store -> this hook.
 * Pass `enableSocket` ONLY if no navbar in the layout already opens the
 * socket (Navbar does, via useNotifications). Default is false to avoid a
 * duplicate connection.
 *
 * @param {"patient"|"doctor"} role
 * @param {{messages?:string, appointments?:string, waitingRoom?:string}} routes
 *        Sidebar route for each indicator; visiting it acknowledges the events.
 */
export default function useSidebarIndicators({
  role,
  routes = {},
  enableSocket = false,
} = {}) {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const items = useSelector(selectItems);
  const principalId = useSelector(selectPrincipalUserId);
  const effectiveId = useSelector(selectEffectiveUserId);
  const patientUser = useSelector(selectAuthUser);
  const doctorUser = useSelector(
    (s) => s.doctor?.user || s.doctor?.currentUser || null
  );

  const userId = useMemo(() => {
    if (role === "doctor") {
      return normId(
        doctorUser?.id ?? doctorUser?.userId ?? doctorUser?.uuid ?? doctorUser?.pgId ?? doctorUser?.doctorId
      );
    }
    return normId(principalId ?? patientUser?.id ?? patientUser?.userId ?? patientUser?.uuid);
  }, [role, doctorUser, principalId, patientUser]);

  const subprofileId = useMemo(() => {
    if (role !== "patient") return null;
    const e = normId(effectiveId);
    const p = normId(principalId ?? patientUser?.id ?? patientUser?.userId);
    return e && p && e !== p ? e : null;
  }, [role, effectiveId, principalId, patientUser]);

  // Optional socket (see JSDoc)
  useNotificationsSocket({
    enabled: !!enableSocket && !!userId,
    roleOverride: role,
    userIdOverride: userId,
  });

  const { counts, idsByKey } = useMemo(
    () => computeIndicators({ items, role, userId, subprofileId }),
    [items, role, userId, subprofileId]
  );

  /* ---- Acknowledge when the user is looking at the section ---- */
  const [tabVisible, setTabVisible] = useState(
    typeof document === "undefined" || document.visibilityState === "visible"
  );
  useEffect(() => {
    const onVis = () => setTabVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const inFlight = useRef(new Set());

  useEffect(() => {
    if (!tabVisible) return; // don't clear events while the tab is in background

    Object.values(INDICATOR).forEach((key) => {
      const route = routes[key];
      if (!route || !isUnderRoute(pathname, route)) return;

      (idsByKey[key] || []).forEach((id) => {
        if (inFlight.current.has(id)) return;
        inFlight.current.add(id);
        Promise.resolve(dispatch(markNotificationRead({ id, userId, role })))
          .catch(() => {})
          .finally(() => inFlight.current.delete(id));
      });
    });
    // routes is a fresh object each render; its values are what matter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, idsByKey, tabVisible, dispatch, userId, role,
      routes.messages, routes.appointments, routes.waitingRoom]);

  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return { counts, total };
}